import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/lib/api/auth.api';
import { useAuth } from '@/lib/auth/auth-context';
import { useWorkspaceApplications } from '@/lib/applications/workspace-applications';
import type { MembershipRole } from '@/lib/api/types';

export type AdminConsoleSection = 'dashboard' | 'users' | 'organizations' | 'authorization' | 'applications' | 'audit' | 'profile';

type RoleName = 'platform-admin' | 'platform-operator' | 'auditor';

const SECTION_ROLES: Record<AdminConsoleSection, readonly RoleName[]> = {
  dashboard: ['platform-admin', 'platform-operator', 'auditor'],
  users: ['platform-admin', 'platform-operator'],
  organizations: ['platform-admin', 'platform-operator'],
  authorization: ['platform-admin', 'platform-operator'],
  applications: ['platform-admin', 'platform-operator'],
  audit: ['platform-admin', 'auditor'],
  profile: ['platform-admin', 'platform-operator', 'auditor'],
};

function hasAnyRole(userRoles: readonly string[], allowedRoles: readonly RoleName[]): boolean {
  return allowedRoles.some((role) => userRoles.includes(role));
}

function parseBase64UrlJson(value: string): Record<string, unknown> | null {
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(window.atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function decodeJwtPayload(token: string | undefined): Record<string, unknown> | undefined {
  if (!token) {
    return undefined;
  }

  const [, payload] = token.split('.');
  if (!payload) {
    return undefined;
  }

  return parseBase64UrlJson(payload) ?? undefined;
}

export function extractRolesFromClaims(profile: Record<string, unknown> | undefined): string[] {
  if (!profile) {
    return [];
  }

  const realmAccess = profile.realm_access;
  const realmRoles = realmAccess && typeof realmAccess === 'object' && Array.isArray((realmAccess as { roles?: unknown }).roles)
    ? (realmAccess as { roles: unknown[] }).roles.filter((role): role is string => typeof role === 'string')
    : [];

  const resourceAccess = profile.resource_access;
  const clientRoles =
    resourceAccess && typeof resourceAccess === 'object'
      ? Object.values(resourceAccess as Record<string, unknown>).flatMap((entry) => {
        if (!entry || typeof entry !== 'object' || !Array.isArray((entry as { roles?: unknown }).roles)) {
          return [];
        }
        return (entry as { roles: unknown[] }).roles.filter((role): role is string => typeof role === 'string');
      })
      : [];

  return [...new Set([...realmRoles, ...clientRoles])];
}

export function deriveAdminConsoleAccess(
  roles: readonly string[] = [],
  _options?: { activeOrganizationMembershipRole?: MembershipRole | null; hasAdminConsoleApplication?: boolean },
) {
  const resolvedRoles = [...roles];
  const hasAdminConsoleRole = hasAnyRole(resolvedRoles, SECTION_ROLES.dashboard);

  return {
    dashboard: hasAdminConsoleRole,
    users: hasAnyRole(resolvedRoles, SECTION_ROLES.users),
    organizations: hasAnyRole(resolvedRoles, SECTION_ROLES.organizations),
    authorization: hasAnyRole(resolvedRoles, SECTION_ROLES.authorization),
    applications: hasAnyRole(resolvedRoles, SECTION_ROLES.applications),
    audit: hasAnyRole(resolvedRoles, SECTION_ROLES.audit),
    profile: true,
  };
}

export function useAdminConsoleAccess() {
  const auth = useAuth();
  const workspaceApplications = useWorkspaceApplications({
    enabled: !auth.isLoading && auth.isAuthenticated,
  });
  const { data: myOrganizations, isLoading: organizationsLoading } = useQuery({
    queryKey: ['auth', 'organizations'],
    queryFn: authApi.organizations,
    enabled: !auth.isLoading && auth.isAuthenticated,
    staleTime: 30_000,
  });
  const accessTokenClaims = decodeJwtPayload(auth.user?.access_token);
  const profileClaims = auth.user?.profile as Record<string, unknown> | undefined;
  const roles = extractRolesFromClaims(accessTokenClaims).length > 0
    ? extractRolesFromClaims(accessTokenClaims)
    : extractRolesFromClaims(profileClaims);
  const activeOrganizationEntry =
    myOrganizations?.organizations.find((organization) => organization.organizationId === myOrganizations.activeOrganizationId) ?? null;
  const hasAdminConsoleApplication = workspaceApplications.hasApplication('admin-bff');
  const derivedAccess = deriveAdminConsoleAccess(roles, {
    activeOrganizationMembershipRole: activeOrganizationEntry?.membershipRole ?? null,
    hasAdminConsoleApplication,
  });
  const access = derivedAccess;

  return {
    access,
    hasAdminConsoleApplication,
    isLoading: auth.isLoading || workspaceApplications.isLoading || organizationsLoading,
    error: auth.error,
    isUnauthorized: false,
    isForbidden: false,
    isAuthenticated: auth.isAuthenticated,
    isAuthLoading: auth.isLoading,
  };
}
